import classnames from 'classnames';
import type { FunctionComponent, MouseEvent } from 'react';

import { useBooleanFlags } from '@/core/flags/flags.hooks';
import { PLATFORMS } from '@/core/settings/constants';
import { getPlatform } from '@/core/settings/utils';
import { CertificationsFilters } from '@/domains/productDiscovery/Listings/components/Filters/components/CertificationsFilters/CertificationsFilters';
import { DeliveryFilter } from '@/domains/productDiscovery/Listings/components/Filters/components/DeliveryFilter/DeliveryFilter';
import { RangeFilter } from '@/domains/productDiscovery/Listings/components/Filters/components/RangeFilter/RangeFilter';
import { FiltersPanelHeader } from '@/productDiscovery/Listings/components/FiltersPanel/components/FiltersPanelHeader/FiltersPanelHeader';
import { FiltersPanel } from '@/productDiscovery/Listings/components/FiltersPanel/FiltersPanel';
import { useNbProducts } from '@/productDiscovery/Listings/hooks/useNbProducts';

import { StringABTests } from '../../constants';
import { useNbRefinedFacets } from '../../facets/hooks/useNbRefinedFacets';
import { ProductCardTag } from '../ProductCardListingsEnhancer/components/ProductCardListings/interfaces';
import { ActiveFilters } from './components/ActiveFilters/ActiveFilters';
import { AdvantagesFilter } from './components/AdvantagesFilter/AdvantagesFilter';
import { CategoriesFilter } from './components/CategoriesFilter/CategoriesFilter';
import { MultiSelectFilter } from './components/MultiSelectFilter/MultiSelectFilter';
import { RatingsFilter } from './components/RatingsFilter/RatingsFilter';
import { useCertificationsFiltersConfigs } from './hooks/useCertificationsFiltersConfigs';
import { useFilterCount } from './hooks/useFilterCount';
import { useOrderedFilterConfigs } from './hooks/useOrderedFilterConfigs';

import styles from './Filters.module.scss';

export interface FiltersProps {
  className?: string;
  open?: boolean;
  onClose?(event: MouseEvent): void;
}

export const Filters: FunctionComponent<FiltersProps> = ({
  className,
  open,
  onClose,
}) => {
  const activeFiltersLength = useNbRefinedFacets();
  const nbProducts = useNbProducts();
  const filterConfigs = useOrderedFilterConfigs();
  const filterCount = useFilterCount(filterConfigs);
  const certificationsFiltersConfigs =
    useCertificationsFiltersConfigs(filterConfigs);
  const [mergedFilters, brandFilterRevamp] = useBooleanFlags([
    StringABTests.MERGED_FILTERS,
    StringABTests.BRAND_FILTER_REVAMP,
  ]);

  let position = 1;
  const generatePosition = () => {
    position += 1;
    return position;
  };

  return (
    <FiltersPanel
      className={classnames(styles.root, className)}
      open={open}
      productsCount={nbProducts ?? 0}
      onClose={onClose}
    >
      <FiltersPanelHeader onClose={onClose}>
        {activeFiltersLength > 0 && <ActiveFilters />}
      </FiltersPanelHeader>

      <DeliveryFilter
        position={generatePosition()}
        className={styles.firstFilter}
      />

      {filterConfigs.map((filterConfig) => {
        const {
          attributeName,
          hidden,
          mergedValues,
          selected,
          translations,
          type,
          urlKey,
        } = filterConfig;

        if (hidden) return null;

        if (type === 'custom' && attributeName === 'promotions') {
          return (
            <AdvantagesFilter
              key={attributeName}
              facetName={attributeName}
              filterCount={filterCount}
              tagName={filterConfig.tagName as ProductCardTag}
              title={translations?.label as string}
              position={generatePosition()}
              urlKey={urlKey}
            />
          );
        }

        if (type === 'range') {
          return (
            <RangeFilter
              key={attributeName}
              facetName={attributeName}
              title={translations?.label as string}
              position={generatePosition()}
              trackingId={urlKey}
              filterCount={filterCount}
              type={urlKey}
            />
          );
        }

        if (type === 'checkbox' && attributeName === 'rating_rounded_down') {
          return (
            <RatingsFilter
              key={attributeName}
              facetName={attributeName}
              title={translations?.label as string}
              position={generatePosition()}
              filterCount={filterCount}
            />
          );
        }

        if (type === 'checkbox' && attributeName === 'categories.last') {
          return (
            <CategoriesFilter
              key={attributeName}
              facetName={attributeName}
              title={translations?.label as string}
              position={generatePosition()}
              filterCount={filterCount}
            />
          );
        }

        if (type === 'checkbox' || type === 'color') {
          const isMultiSelectRevamp =
            brandFilterRevamp && attributeName === 'brand_name';

          return (
            <MultiSelectFilter
              key={attributeName}
              facetName={attributeName}
              title={translations?.label as string}
              position={generatePosition()}
              filterCount={filterCount}
              type={type}
              mergedValues={mergedFilters ? mergedValues : undefined}
              selection={isMultiSelectRevamp ? selected : undefined}
              searchBar={isMultiSelectRevamp}
            />
          );
        }

        return null;
      })}

      {getPlatform() === PLATFORMS.FRANCE &&
        certificationsFiltersConfigs.length > 0 && (
          <CertificationsFilters
            filtersConfigs={certificationsFiltersConfigs}
            filterCount={filterCount}
            position={generatePosition()}
          />
        )}
    </FiltersPanel>
  );
};

Filters.displayName = 'Filters';
